// @ts-nocheck
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Search } from 'lucide-react';
import { selectAllProducts } from '../stores/menu/productsSlice';
import { useLanguageContext } from "../context/languageContext";

export default function Menu() {
  const { t } = useLanguageContext();
  const [searchTerm, setSearchTerm] = useState('');

  // Menu comes from the bundled snapshot — always present, never loading.
  const menuData = useSelector(selectAllProducts);

  const hasDessert = menuData.some(cat => cat.categoryName?.toLowerCase() === 'dessert');
  const [selectedCategory, setSelectedCategory] = useState(hasDessert ? 'Dessert' : 'Të gjitha');

  // Get all category names
  const categories = ['Të gjitha', ...menuData.map(cat => cat.categoryName)];

  // Filter items based on selected category and search term
  const getFilteredData = () => {
    const matches = (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const visible =
      selectedCategory === 'Të gjitha'
        ? menuData
        : menuData.filter(cat => cat.categoryName === selectedCategory);

    return visible
      .map(category => ({
        id: category.id,
        categoryName: category.categoryName,
        items: category.menuItems.filter(matches),
      }))
      .filter(cat => cat.items.length > 0);
  };

  const filteredData = getFilteredData();
  const totalItems = filteredData.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <section id="menu" className="py-12 sm:py-20 bg-cream">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-16 animate-fade-in-up">
          <div className="inline-block px-4 py-1.5 border border-gold/30 rounded-full mb-4">
            <span className="text-xs font-bold text-gold-deep tracking-widest uppercase">{t("Our Menu")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-dark-chocolate mb-3 sm:mb-6 px-2">
            {t("Delicious")} <span className="italic gradient-text-gold-deep">{t("Desserts")}</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto px-4 font-light text-sm sm:text-lg">
            {t("Discover our collection of handcrafted pastries and authentic gelato.")}
          </p>
        </div>

        {/* Search and Category Filter */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
          <div className="relative mb-5 sm:mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gold-deep w-5 h-5 transition-transform duration-300 peer-focus:scale-110" />
            <input
              type="text"
              placeholder={t("Search for pastries...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="peer w-full pl-11 pr-4 py-3 sm:py-4 text-sm sm:text-base bg-white border border-gray-100 rounded-xl focus:outline-none focus:border-gold focus:shadow-lg focus:shadow-gold/20 transition-all duration-300 shadow-sm text-dark-chocolate placeholder-gray-400"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-2 text-xs sm:px-6 sm:py-3 sm:text-sm tracking-wide uppercase font-bold transition-all duration-300 rounded-xl ${selectedCategory === category
                  ? 'bg-dark-chocolate text-gold shadow-lg transform -translate-y-1 scale-105'
                  : 'bg-white text-gray-500 hover:text-dark-chocolate hover:shadow-md hover:scale-105 border border-gray-100'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Items Grouped by Category */}
        <div className="max-w-7xl mx-auto">
          {filteredData.map((categoryGroup, groupIndex) => (
            <div key={groupIndex} className="mb-10 sm:mb-16">
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-dark-chocolate mb-4 sm:mb-8 pb-2 sm:pb-4 border-b border-gold/20">
                {categoryGroup.categoryName}
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                {categoryGroup.items.map((item) => (
                  <div
                    key={item.id}
                    className="group bg-white rounded-xl shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-3"
                  >
                    <div className="relative h-40 sm:h-64 overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-chocolate/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-dark-chocolate text-xs font-bold uppercase tracking-wider shadow-sm group-hover:bg-gold group-hover:text-white transition-colors duration-300">
                        {categoryGroup.categoryName}
                      </div>
                    </div>

                    {/* Tight on phones, roomier from sm up: full padding and a
                        fixed description height waste most of a 160px card. */}
                    <div className="p-3 sm:p-6">
                      <h3 className="text-base sm:text-lg font-serif font-bold text-dark-chocolate mb-1 sm:mb-2 line-clamp-2">
                        {item.name}
                      </h3>
                      {/* Most items carry a blank description (" "); render the
                          paragraph only when there is something real to show
                          rather than padding the card with filler. */}
                      {item.description?.trim() && (
                        <p className="text-gray-500 text-xs sm:text-sm leading-snug sm:leading-relaxed mb-2 sm:mb-4 line-clamp-2">
                          {item.description.trim()}
                        </p>
                      )}
                      <div className="pt-2 sm:pt-4 border-t border-gray-50">
                        <span className="text-lg sm:text-xl font-bold text-gold-deep">
                          {item.price} ALL
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {totalItems === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg font-serif italic">
              {t("No items found. Please try a different category or search term.")}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
